import { TradeablePlan } from '../trade';
import { Price } from '../entity-full';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Tradeable-object type ids that represent promotion schedules. */
export const SLIDER_TOTID = 7;
export const ADVERT_TOTID = 8;

/** Default `totid` filter sent to the catalog endpoint (sliders + adverts). */
export const PROMOTION_TOTIDS = `${SLIDER_TOTID},${ADVERT_TOTID}`;

/** `prictid` of the list price (the price consumers actually pay). */
export const LIST_PRICE_PRICTID = 1;

/** Default currency / country the admin pricing screen operates on. */
export const DEFAULT_CCODE = 'GHS';
export const DEFAULT_COUNTRY_ISO2 = 'GH';

/** Human-readable labels per promotion (tradeable-object) type id. */
export const PROMOTION_TYPE_LABELS: Record<number, string> = {
  [SLIDER_TOTID]: 'Sliders',
  [ADVERT_TOTID]: 'Adverts',
};

/** Best-effort map from currency code → ISO-2 country. */
export const CCODE_COUNTRY_ISO2: Record<string, string> = {
  GHS: 'GH',
};

// ─── API Payloads ─────────────────────────────────────────────────────────────

/** Idempotent upsert body for `POST /api/admin/prices`. */
export interface UpsertPricePayload {
  prictid: number; // 1 = list price
  toid: number; // tradeable object (plan) id
  ccode: string; // e.g. "GHS"
  country_iso2: string; // e.g. "GH"
  price: number;
  /** Acting admin's uid. Required by the backend `createPrice` (missing_fields otherwise). */
  uid_actor: number;
}

/** Partial update body for `PUT /api/admin/prices/<pricid>`. */
export interface UpdatePricePayload {
  price?: number;
  ccode?: string;
  country_iso2?: string;
  prictid?: number;
  /** Acting admin's uid — attributed on the price-history row when the amount changes. */
  uid_actor?: number;
}

// ─── Tradeable-object (plan) config payloads ────────────────────────────────────
// Sent to `POST /api/admin/tradeable_objects/` (create) and
// `PUT /api/admin/tradeable_objects/<toid>` (update). The backend silently drops
// unknown keys and enforces no requiredness — send numeric 0/1 for flags, real
// arrays/objects (not the "None" sentinel), and a FLAT `parameters` object.

/** 0/1 flag as the backend stores TINYINT(1) columns. */
export type Flag = 0 | 1;

/** Configurable fields shared by create + update. */
export interface TradeableObjectConfig {
  name?: string;
  name_type?: string;
  description?: string;
  description_json?: string[]; // feature bullets
  totid?: number; // tradeable-object type
  tocid?: number; // collection (optional; no admin lookup yet)
  topid?: number; // package (optional; no admin lookup yet)
  tier?: number;
  order_by?: number;
  sub_duration?: number; // days
  country_iso2?: string;

  // Audience flags
  for_user?: Flag;
  for_org?: Flag;
  for_biz?: Flag;
  for_ext_link?: Flag;

  // Status flags
  active?: Flag;
  popular?: Flag;
  featured?: Flag;
  stackable?: Flag;
  upgradeable?: Flag;
  atomic?: Flag;
  trader_plan?: Flag;
  shopper_plan?: Flag;

  etid?: number[];
  parameters?: Record<string, string | number>; // flat key/value
}

/** `POST /api/admin/tradeable_objects/` — `name`/`totid`/`country_iso2` expected in practice. */
export type CreateTradeableObjectPayload = TradeableObjectConfig;

/** `PUT /api/admin/tradeable_objects/<toid>` — partial config patch. */
export type UpdateTradeableObjectPayload = TradeableObjectConfig;

/** Wrapper returned by the tradeable-object create/update endpoints. */
export interface TradeableObjectMutationResponse {
  status: number;
  data: { toid: number; uuid?: string; [k: string]: unknown };
}

// ─── API Responses ────────────────────────────────────────────────────────────

/**
 * `GET /api/admin/tradeable_objects?...&include_prices=1`
 * Catalog is nested under `data.tradeable_objects` (not a bare array).
 */
export interface PromotionPricingResponse {
  status: number;
  data: { tradeable_objects: TradeablePlan[] };
}

/** Wrapper returned by the price upsert/update endpoints. */
export interface PriceMutationResponse {
  status: number;
  data: Price;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Find the list price row for a plan in the given currency. Falls back to any
 * row in that currency, then to the first available price.
 */
export const getListPrice = (
  plan: TradeablePlan,
  ccode: string = DEFAULT_CCODE,
): Price | undefined => {
  const prices = plan.prices ?? [];
  const matchesCcode = (p: Price) => (p.ccode ?? p.currency) === ccode;
  return (
    prices.find((p) => (p.prictid ?? LIST_PRICE_PRICTID) === LIST_PRICE_PRICTID && matchesCcode(p)) ??
    prices.find(matchesCcode) ??
    prices[0]
  );
};
